import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { UserProfileDto, UserRole } from "@green0meter/api-client";

import { DOMAIN_FORMS_CONFIG } from "../domain";

import { ACTION_GET_FORM_SECTIONS, DOMAIN_FORMS, roleGuard } from "@api";
import { formsConfigApi } from "@api/services";

const ACTION_SORT_FORM_SECTIONS = "SORT_FORM_SECTIONS";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type SortFormSectionsProps = {
  formId: string;
  dto: {
    sectionIds: string[];
  };
};

const sortFormSections = async (props: SortFormSectionsProps): Promise<void> => {
  const response = await formsConfigApi.formsConfigControllerSortFormSections(
    props.formId,
    props.dto,
  );

  return response.data;
};

export const useSortFormSections = (
  user: UserProfileDto | null,
  formId: string,
): {
  sortFormSections: UseMutateFunction<void, Error, SortFormSectionsProps, unknown>;
  sortFormSectionsAsync: (props: SortFormSectionsProps) => Promise<void>;
  sortFormSectionsEnabled: boolean;
  isSuccessSortFormSections: boolean;
  isPendingSortFormSections: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORMS_CONFIG, ACTION_SORT_FORM_SECTIONS],
    mutationFn: enabled ? sortFormSections : undefined,
    onSuccess() {
      const queryKey = [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId];

      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    sortFormSections: mutate,
    sortFormSectionsAsync: mutateAsync,
    sortFormSectionsEnabled: enabled,
    isSuccessSortFormSections: isSuccess,
    isPendingSortFormSections: isPending,
  };
};
